import { useState } from 'react';
import {
  Button,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { CheckIcon, CopyIcon, DownloadSimpleIcon, ImageIcon, WarningIcon } from '@phosphor-icons/react';
import { Alert } from '@mantine/core';
import { type FaviconOutput, generateFavicons, loadImage, zipFavicons } from '@/lib/favicon';
import { useStrings } from '@/i18n/useLang';

function downloadBytes(filename: string, bytes: Uint8Array, mime: string) {
  const blob = new Blob([bytes.slice()], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function CopyBox({ label, value }: { label: string; value: string }) {
  const t = useStrings();
  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Text fw={600} size="sm">
          {label}
        </Text>
        <CopyButton value={value} timeout={1500}>
          {({ copied, copy }) => (
            <Button variant="subtle" size="xs" leftSection={copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />} onClick={copy}>
              {copied ? t.ui.copied : t.ui.copy}
            </Button>
          )}
        </CopyButton>
      </Group>
      <Textarea value={value} readOnly autosize minRows={2} maxRows={8} styles={{ input: { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
    </div>
  );
}

export function FaviconGenerator() {
  const t = useStrings();
  const [output, setOutput] = useState<FaviconOutput | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const img = await loadImage(file);
      setOutput(await generateFavicons(img));
    } catch {
      setError(t.ui.error);
      setOutput(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="lg">
      <Dropzone onDrop={(files) => files[0] && onFile(files[0])} accept={IMAGE_MIME_TYPE} multiple={false} loading={busy} radius="md">
        <Group justify="center" gap="sm" mih={120} style={{ pointerEvents: 'none' }}>
          <ImageIcon size={32} color="var(--primary)" />
          <div>
            <Text fw={600}>{t.ui.dropTitle}</Text>
            <Text size="sm" c="dimmed">
              {t.ui.dropHint}
            </Text>
          </div>
        </Group>
      </Dropzone>

      {error && (
        <Alert color="red" icon={<WarningIcon size={18} />} variant="light">
          {error}
        </Alert>
      )}

      {output && (
        <Stack gap="md">
          <Group justify="center" gap="lg" align="flex-end">
            {output.previews.map((p) => (
              <Stack key={p.size} gap={4} align="center">
                <Paper withBorder p={6} radius="sm" style={{ background: 'var(--white)' }}>
                  <img src={p.url} alt={`${p.size}px`} width={p.size > 64 ? 64 : p.size} height={p.size > 64 ? 64 : p.size} />
                </Paper>
                <Text size="xs" c="dimmed">
                  {p.size}px
                </Text>
              </Stack>
            ))}
          </Group>

          <Button
            leftSection={<DownloadSimpleIcon size={18} />}
            onClick={() => downloadBytes('favicons.zip', zipFavicons(output.files), 'application/zip')}
            size="md"
          >
            {t.ui.downloadZip}
          </Button>

          <Group gap="xs">
            {Object.entries(output.files).map(([name, bytes]) => (
              <Button
                key={name}
                variant="default"
                size="xs"
                leftSection={<DownloadSimpleIcon size={14} />}
                onClick={() => downloadBytes(name, bytes, name.endsWith('.ico') ? 'image/x-icon' : name.endsWith('.webmanifest') ? 'application/manifest+json' : 'image/png')}
              >
                {name}
              </Button>
            ))}
          </Group>

          <CopyBox label={t.ui.snippet} value={output.snippet} />
          <CopyBox label={t.ui.manifest} value={output.manifest} />
        </Stack>
      )}
    </Stack>
  );
}
